import { Link } from "react-router-dom";
import { AlertTriangle, EyeOff, Lightbulb, Sparkles, ArrowRight } from "lucide-react";
import type { QualityReport } from "@/hooks/useProperties";
import type { QualityRuleKey } from "@/hooks/useQualityConfig";
import { QUALITY_RULE_INFO, DESCRIPTION_RULE_KEYS } from "@/lib/qualityRules";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Owner/admin-facing banner explaining why a listing is hidden (failed
// deal-breaker checks) or how to make it stronger (soft warnings). Renders
// nothing when quality control is off for the tenant or the listing passes
// everything. Reads the materialized `quality_report`, no extra fetch.

interface Props {
  report: QualityReport | null;
  /** The listing's materialized quality_visible flag. */
  visible: boolean;
  /** Link to the edit page (shown when no inline revise handler is given). */
  editHref?: string;
  /** Inline "Revise by AI" handler (EditProperty wires this to the textarea). */
  onReviseDescription?: () => void;
  className?: string;
}

interface FailItem {
  key: QualityRuleKey;
  dealBreaker: boolean;
}

const ListingQualityBanner = ({ report, visible, editHref, onReviseDescription, className }: Props) => {
  // Gate off for this tenant, or never evaluated → nothing to show.
  if (!report?.enabled || !report.rules) return null;

  const fails: FailItem[] = Object.entries(report.rules)
    .filter(([, r]) => r && r.pass === false)
    .map(([key, r]) => ({ key: key as QualityRuleKey, dealBreaker: !!r.dealBreaker }));

  if (fails.length === 0) return null;

  const dealBreakers = fails.filter((f) => f.dealBreaker);
  const soft = fails.filter((f) => !f.dealBreaker);
  const isHidden = !visible || dealBreakers.length > 0;
  const hasDescriptionIssue = fails.some((f) => DESCRIPTION_RULE_KEYS.includes(f.key));

  // Deal-breakers lead (they're why it's hidden); soft warnings follow.
  const ordered = [...dealBreakers, ...soft];

  return (
    <div
      className={cn(
        "rounded-xl border p-4 text-left",
        isHidden
          ? "border-destructive/30 bg-destructive/5"
          : "border-amber-400/40 bg-amber-50 dark:bg-amber-950/20",
        className,
      )}
    >
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 shrink-0">
          {isHidden ? (
            <EyeOff className="h-4.5 w-4.5 text-destructive" />
          ) : (
            <Lightbulb className="h-4.5 w-4.5 text-amber-600" />
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2.5">
          <div>
            <p className={cn("text-sm font-semibold", isHidden ? "text-destructive" : "text-amber-700 dark:text-amber-400")}>
              {isHidden ? "Hidden from renters: needs improvements to go live" : "Make this listing stronger"}
            </p>
            <p className="text-[12.5px] text-muted-foreground mt-0.5">
              {isHidden
                ? "Renters and the AI concierge can’t see this listing until the required checks below pass."
                : "These optional checks don’t hide your listing, but fixing them helps it perform better."}
            </p>
          </div>

          <ul className="space-y-1.5">
            {ordered.map(({ key, dealBreaker }) => {
              const info = QUALITY_RULE_INFO[key];
              if (!info) return null;
              return (
                <li key={key} className="flex items-start gap-2 text-[12.5px]">
                  <AlertTriangle
                    className={cn("h-3.5 w-3.5 mt-0.5 shrink-0", dealBreaker ? "text-destructive" : "text-amber-500")}
                  />
                  <span>
                    <span className="font-medium text-foreground">{info.label}.</span>{" "}
                    <span className="text-muted-foreground">{info.fix}</span>
                  </span>
                </li>
              );
            })}
          </ul>

          {(hasDescriptionIssue || editHref) && (
            <div className="flex flex-wrap items-center gap-2 pt-0.5">
              {hasDescriptionIssue && onReviseDescription && (
                <Button size="sm" variant="outline" className="h-8" onClick={onReviseDescription}>
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Revise by AI
                </Button>
              )}
              {editHref && (
                <Button size="sm" variant={isHidden ? "default" : "outline"} className="h-8" asChild>
                  <Link to={editHref}>
                    Edit listing <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                  </Link>
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListingQualityBanner;
